import { FC, useMemo } from "react";
import { useTranslation } from "react-i18next";

import { useFormats } from "@ryde/shared";

import {
  RIDE_INVESTIGATION_EVENT_ICON,
  RIDE_INVESTIGATION_SEVERITY_BG,
  RIDE_INVESTIGATION_SEVERITY_COLOR,
} from "../investigation.constants";
import { IPlaybackModel } from "../investigation.models";
import {
  EventRowStyled,
  PanelMessageStyled,
  PanelSectionStyled,
} from "../investigation.panel.styled";
import { buildEventMeta, eventSubject } from "../investigation.utils";
import { usePlaybackContext } from "../playback/ride-investigation-playback.context";
import { usePlaybackSnapshot } from "../playback/use-playback-snapshot";

/** Chronological event timeline; each row seeks the playhead to that moment. */
export const InvestigationEventFeed: FC<{ model: IPlaybackModel }> = ({
  model,
}) => {
  const { t } = useTranslation();
  const { formatTimeWithSecondsNormalized } = useFormats();
  const { seekToMs } = usePlaybackContext();
  const snapshot = usePlaybackSnapshot();
  const currentMs = snapshot?.tMs;

  // The "current" event is the last one the playhead has reached (events are time-sorted).
  const activeEventId = useMemo(() => {
    if (currentMs === undefined) {
      return undefined;
    }

    let activeId: string | undefined;

    for (const entry of model.events) {
      if (entry.tMs <= currentMs) {
        activeId = entry.event.id;
      } else {
        break;
      }
    }

    return activeId;
  }, [model.events, currentMs]);

  if (!model.events.length) {
    return (
      <PanelMessageStyled>
        {t("rideMap.investigation.noEvents")}
      </PanelMessageStyled>
    );
  }

  return (
    <PanelSectionStyled>
      {model.events.map(entry => {
        const Icon = RIDE_INVESTIGATION_EVENT_ICON[entry.event.type];
        const meta = buildEventMeta(entry.event);

        return (
          <EventRowStyled
            key={entry.event.id}
            $color={RIDE_INVESTIGATION_SEVERITY_COLOR[entry.event.severity]}
            $bg={RIDE_INVESTIGATION_SEVERITY_BG[entry.event.severity]}
            $active={entry.event.id === activeEventId}
            onClick={() => seekToMs(entry.tMs)}
          >
            <span className="event-icon">
              <Icon />
            </span>
            <div className="event-body">
              <div className="event-label">
                {t(entry.event.labelKey)}
                {eventSubject(entry.event)}
              </div>
              {meta && <div className="event-meta">{meta}</div>}
            </div>
            <span className="event-time">
              {formatTimeWithSecondsNormalized(entry.event.timestamp)}
            </span>
          </EventRowStyled>
        );
      })}
    </PanelSectionStyled>
  );
};
